/**
 * LaunchPage — Hub central de Polynator
 *
 * Ruta: /launch
 *
 * Hub para acceder a todos los módulos internos y herramientas externas.
 * Accesible desde el login normal (Redux) o desde el SSO Launcher (localStorage).
 */

import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';

import { useRoleForTools } from '@domains/user';
import { setDefaultState } from '@domains/user/store/slices/user-slice/user-slice';
import { getCurrentEnvironment } from '../../config/external-tools';
import { openExternalTool } from '../../utils/external-tool.service';
import { clearLaunchAuth } from './launch.utils';

import { useAuthGuard }      from './hooks/useAuthGuard';
import { useVisibleModules } from './hooks/useVisibleModules';
import { LaunchNavBar }      from './components/LaunchNavBar';
import { ModulesPanel }      from './components/ModulesPanel';
import { ExternalToolsPanel } from './components/ExternalToolsPanel';
import { PageWrapper, PageFooter } from './styles';

export const LaunchPage = () => {
  const navigate              = useNavigate();
  const dispatch              = useDispatch();
  const { ready, email }      = useAuthGuard();
  const visibleModules        = useVisibleModules();
  const env                   = getCurrentEnvironment();

  useRoleForTools();

  const handleOpenInternal = (path: string) => navigate(path);

  const handleLogout = () => {
    clearLaunchAuth();
    dispatch(setDefaultState());
    navigate('/launch-login', { replace: true });
  };

  if (!ready) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <PageWrapper>
      <LaunchNavBar
        visibleModules={visibleModules}
        onOpenModule={handleOpenInternal}
        onOpenTool={openExternalTool}
        onLogout={handleLogout}
      />

      <Container style={{ paddingTop: '7.5rem', paddingBottom: '3rem' }}>
        <Row className="g-3">

          {!!email && (
            <Col xs={12} md={7}>
              <ModulesPanel modules={visibleModules} onOpenModule={handleOpenInternal} />
            </Col>
          )}

          <Col xs={12} md={!!email ? 5 : 12} style={{ alignSelf: 'flex-start' }}>
            <ExternalToolsPanel />
          </Col>

        </Row>
      </Container>

      {env === 'local' && (
        <PageFooter>
          <strong>Modo Localhost</strong> — Asegúrate de que las herramientas estén corriendo en sus puertos locales.
        </PageFooter>
      )}
    </PageWrapper>
  );
};
